import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { FieldError, SubmitHandler, useForm } from 'react-hook-form';
import { CalendarProps } from 'types/props';
import { getLocalDateString } from 'utils/date-utils';

import { CalendarService } from 'components/services';

import { CalendarSchema } from '../constants/schemas';
import {
  Calendar as CalendarType,
  SpecialDayTypeEnum
} from '../types/interfaces';

import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';
import { withDialogFooterSpacing } from './shared/dialogFooterSpacing';

import './css/CalendarCard.css';

interface RefInterface {
  handleCancel: () => void;
}

enum WeekDayEnum {
  Monday = 0,
  Tuesday = 1,
  Wednesday = 2,
  Thursday = 3,
  Friday = 4,
  Saturday = 5,
  Sunday = 6
}

const specialDayTypeMap = {
  [SpecialDayTypeEnum.DayOff]: 'Выходной',
  [SpecialDayTypeEnum.WeeklyDayOff]: 'Еженедельный выходной',
  [SpecialDayTypeEnum.Working]: 'Рабочий',
  [SpecialDayTypeEnum.WeeklyWorking]: 'Еженедельный рабочий',
  [SpecialDayTypeEnum.Holiday]: 'Праздничный',
  [SpecialDayTypeEnum.WeeklyHoliday]: 'Еженедельный праздничный',
  [SpecialDayTypeEnum.HalfDay]: 'Сокращенный',
  [SpecialDayTypeEnum.WeeklyHalfDay]: 'Еженедельный сокращенный'
};

const nonWeeklyDayTypes = Object.entries(specialDayTypeMap)
  .filter(([key]) => {
    const value = parseInt(key);
    return [
      SpecialDayTypeEnum.DayOff,
      SpecialDayTypeEnum.Working,
      SpecialDayTypeEnum.Holiday,
      SpecialDayTypeEnum.HalfDay
    ].includes(value);
  })
  .map(([value, label]) => ({
    value: Number(value),
    label
  }));

const CalendarCard = React.forwardRef<RefInterface, CalendarProps>(
  (
    { calendarProps, onAdd, onUpdate, onDelete, closeDialog }: CalendarProps,
    ref: React.Ref<RefInterface>
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      setError,
      reset,
      formState: { errors, isDirty }
    } = useForm<CalendarType>({
      resolver: zodResolver(CalendarSchema),
      defaultValues: calendarProps
    });

    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [newSpecialDayValidation, setNewSpecialDayValidation] =
      useState(false);
    const [newSpecialDayHeader, setNewSpecialDayHeader] = useState<
      null | string
    >(null);
    const [showAddSpecialDayDialog, setShowAddSpecialDayDialog] =
      useState(false);
    const [newSpecialDay, setNewSpecialDay] = useState<{
      date: Date | null;
      type: number | null;
    }>({ date: null, type: null });
    const [loading, setLoading] = useState(false);

    const toast = useRef<Toast>(null);
    const formValues = watch();
    const weekendDays = (formValues.weeklySpecialDays || [])
      .filter((entry) => entry.type === SpecialDayTypeEnum.WeeklyDayOff)
      .map((entry) => entry.weekDay);
    const specialDays = watch('specialDays');

    useEffect(() => {
      const fetchCalendarData = async () => {
        if (calendarProps.id) {
          setLoading(true);
          try {
            const response = await CalendarService.getById(calendarProps.id);
            if (response.data) {
              reset({
                ...response.data,
                weeklySpecialDays: response.data.weeklySpecialDays || [],
                specialDays: response.data.specialDays || []
              });
            }
          } catch (error) {
            console.error('Error fetching calendar data:', error);
            toast.current?.show({
              severity: 'error',
              summary: 'Ошибка',
              detail: 'Не удалось загрузить данные календаря',
              life: 3000
            });
          } finally {
            setLoading(false);
          }
        }
      };

      fetchCalendarData();
    }, [calendarProps.id, reset]);

    const onSubmit: SubmitHandler<CalendarType> = async (data) => {
      let response;
      if (onUpdate && data.id) {
        response = await onUpdate(data.id, data);
      } else if (onAdd) {
        response = await onAdd({ ...data, id: 0 });
      }
      if (response?.isSuccess) {
        closeDialog?.();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          // Display the server error message or a default message
          detail: 'Не удалось сохранить календарь',
          life: 3000
        });
        if (response?.error === 'SameName') {
          setError('name', {
            type: 'value',
            message:
              'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите название календаря'
          });
        }
      }
    };

    const handleCancel = () => {
      if (isDirty) {
        setShowDialog(true);
      } else {
        closeDialog?.();
      }
    };

    useImperativeHandle(ref, () => ({
      handleCancel
    }));

    const handleWeekendDayToggle = (day: number) => {
      const currentWeeklySpecialDays = formValues.weeklySpecialDays || [];
      const existingEntryIndex = currentWeeklySpecialDays.findIndex(
        (entry) =>
          entry.weekDay === day &&
          entry.type === SpecialDayTypeEnum.WeeklyDayOff
      );

      const newWeeklySpecialDays = [...currentWeeklySpecialDays];

      if (existingEntryIndex !== -1) {
        // Remove existing WeeklyDayOff entry
        newWeeklySpecialDays.splice(existingEntryIndex, 1);
      } else {
        // Add new WeeklyDayOff entry
        newWeeklySpecialDays.push({
          id: 0, // Temporary ID for new entries
          weekDay: day,
          type: SpecialDayTypeEnum.WeeklyDayOff
        });
      }

      setValue('weeklySpecialDays', newWeeklySpecialDays, {
        shouldDirty: true
      });
    };

    const handleCancelAddSpecialDay = () => {
      setNewSpecialDayValidation(false);
      setNewSpecialDayHeader(null);
      setNewSpecialDay({ date: null, type: null });
      setShowAddSpecialDayDialog(false);
    };

    const handleAddSpecialDay = () => {
      if (!newSpecialDay.date || newSpecialDay.type === null) {
        setNewSpecialDayValidation(true);
        return;
      }

      const dateString = getLocalDateString(newSpecialDay.date);
      if (formValues.specialDays?.some((day) => day.day === dateString)) {
        setNewSpecialDayHeader('Эта дата уже добавлена как день исключения');
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Эта дата уже добавлена как день исключения',
          life: 3000
        });
        return;
      }

      const newSpecialDays = [
        ...(formValues.specialDays || []),
        {
          id: 0,
          day: dateString,
          type: newSpecialDay.type
        }
      ];

      setNewSpecialDayValidation(false);
      setNewSpecialDayHeader(null);
      setValue('specialDays', newSpecialDays, { shouldDirty: true });
      setNewSpecialDay({ date: null, type: null });
      setShowAddSpecialDayDialog(false);
    };

    const handleRemoveSpecialDay = (dayType: string) => {
      const newSpecialDays =
        specialDays?.filter((day) => day.day !== dayType) || [];
      setValue('specialDays', newSpecialDays, { shouldDirty: true });
    };

    return (
      <div className="dialog-tabless-card">
        <Toast ref={toast} />
        <div className="gap-3 pb-0">
          <Divider />
          <div className="asset-type-card__column">
            <div className="flex flex-row gap-5">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="name" className="p-0 calendar-card-label">
                    Название<span className="required">*</span>:
                  </label>
                  <div className="col p-0">
                    <InputText
                      className="user-card__input w-full"
                      {...register('name')}
                    />
                  </div>
                </div>
                <p
                  className={`asset-type-card-errors card-errors ${
                    errors.name ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {errors.name ? errors.name.message : ' '}
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="description" className="p-0 calendar-card-label">
                    Описание:
                  </label>
                  <div className="col p-0">
                    <InputText
                      className="user-card__input w-full"
                      {...register('description')}
                    />
                  </div>
                </div>
                <p
                  className={`asset-type-card-errors card-errors ${
                    errors.description ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {errors.description ? errors.description.message : ' '}
                </p>
              </div>
            </div>

            {/* Выходные дни */}

            <div className="field">
              <label>Выходные дни по умолчанию</label>
              <div className="flex gap-1 flex-wrap">
                {Object.values(WeekDayEnum)
                  .filter((v): v is number => typeof v === 'number')
                  .map((day) => (
                    <Button
                      key={day}
                      label={['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'][day]}
                      className={`p-button ${
                        weekendDays.includes(day)
                          ? 'p-button-success'
                          : 'p-button-secondary'
                      }`}
                      onClick={() => handleWeekendDayToggle(day)}
                    />
                  ))}
              </div>
              <p
                className={`detailed-card-errors card-errors ${
                  errors.weeklySpecialDays ? 'text-red-500' : 'text-transparent'
                } `}
              >
                {errors.weeklySpecialDays
                  ? (errors.weeklySpecialDays  as unknown as FieldError).message
                  : ' '}
              </p>
            </div>

            {/* Рабочие дни */}
            <div className="field">
              <label>Рабочие дни по умолчанию</label>
              <div className="flex gap-1 flex-wrap">
                {Object.values(WeekDayEnum)
                  .filter((v): v is number => typeof v === 'number')
                  .map((day) => (
                    <Button
                      key={day}
                      label={['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'][day]}
                      className={`p-button-outlined ${
                        !weekendDays.includes(day)
                          ? 'p-button-success'
                          : 'p-button-secondary'
                      }`}
                      disabled
                    />
                  ))}
              </div>
            </div>

            {/* Дни исключения */}
            <div className="field">
              <div className="flex gap-2 align-items-center mb-2">
                <label style={{ marginRight: '2px' }}>Дни исключения</label>
                <Button
                  label="Добавить"
                  icon="pi pi-plus"
                  className="p-button-sm"
                  onClick={() => setShowAddSpecialDayDialog(true)}
                />
              </div>

              <div
                className="scrollable-container"
                id="tags-scroll"
                style={{
                  width: '100%',
                  height: 'fit-content',
                  maxHeight: '150px',
                  overflow: 'auto'
                }}
              >
                <div
                  className="flex flex-column scrollable-content"
                  style={{ width: '99%' }}
                >
                  <div className="flex flex-column gap-2">
                    {specialDays?.map((day) => (
                      <div
                        key={day.id}
                        style={{ width: '230px' }}
                        className="flex align-items-center justify-content-between pl-2 border-round surface-border border-1"
                      >
                        <span>
                          {`${new Date(
                            day.day
                          ).toLocaleDateString()} - ${nonWeeklyDayTypes.find(
                            (t) => t.value === day.type
                          )?.label}`}
                        </span>
                        <Button
                          icon="pi pi-trash"
                          className="p-button-danger p-button-text"
                          onClick={() => handleRemoveSpecialDay(day.day)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Кнопки действий */}
        <div className="default-card__buttons">
          <div className="flex gap-3">
            <Button
              label="Сохранить"
              icon="pi pi-check"
              onClick={handleSubmit(onSubmit)}
              loading={loading}
            />
            <Button
              label="Удалить"
              icon="pi pi-trash"
              className="p-button-danger"
              onClick={() => setShowDeleteDialog(true)}
              disabled={!calendarProps.id}
            />
            <Button
              label="Отмена"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={handleCancel}
            />
          </div>
        </div>

        {/* Диалоговые окна */}
        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={() => setShowDeleteDialog(false)}
          onConfirm={() => {
            onDelete?.(calendarProps.id);
            closeDialog?.();
          }}
          entityType="календарь"
          entityName={formValues.name || ''}
        />

        <ConfirmationDialog
          visible={showDialog}
          onCancel={() => setShowDialog(false)}
          onContinue={() => {
            setShowDialog(false);
            closeDialog?.();
          }}
        />

        <Dialog
          visible={showAddSpecialDayDialog}
          onHide={() => setShowAddSpecialDayDialog(false)}
          header="Добавить день исключения"
          pt={withDialogFooterSpacing()}
          footer={
            <div className="flex justify-content-end gap-2">
              <Button
                label="Отмена"
                className="p-button-secondary"
                onClick={handleCancelAddSpecialDay}
              />
              <Button
                label="Добавить"
                className="p-button-primary"
                onClick={handleAddSpecialDay}
              />
            </div>
          }
          style={{
            width: '350px'
          }}
        >
          <div className="flex flex-column gap-1">
            <p className="card-errors text-red-500">
              {newSpecialDayHeader && newSpecialDayHeader}
            </p>
            <Calendar
              value={newSpecialDay.date}
              onChange={(e) =>
                setNewSpecialDay((prev) => ({
                  ...prev,
                  date: e.value as Date
                }))
              }
              dateFormat="dd.mm.yy"
              showIcon
              placeholder="Выберите дату"
            />
            <p className="card-errors text-red-500">
              {newSpecialDayValidation && !newSpecialDay.date && 'Укажите дату'}
            </p>
            <Dropdown
              value={newSpecialDay.type}
              options={nonWeeklyDayTypes}
              onChange={(e) =>
                setNewSpecialDay((prev) => ({
                  ...prev,
                  type: e.value,
                  id: 0
                }))
              }
              placeholder="Выберите тип дня"
            />
            <p className="text-red-500">
              {newSpecialDayValidation &&
                newSpecialDay.type === null &&
                'Задайте тип дня'}
            </p>
          </div>
        </Dialog>
      </div>
    );
  }
);

CalendarCard.displayName = 'CalendarCard';

export default CalendarCard;
